/**
 * Shared mutators. Pure functions over row shapes so the client (optimistic apply,
 * rebase) and the server (authoritative apply) compute identical results.
 */
export const SYSTEM = { INBOX: "INBOX", UNREAD: "UNREAD", STARRED: "STARRED", TRASH: "TRASH", SPAM: "SPAM", IMPORTANT: "IMPORTANT" } as const;

export interface LabelDelta {
  add: readonly string[];
  remove: readonly string[];
}

/** Gmail semantics: add wins over nothing, remove of absent is a no-op; result is sorted + unique. */
export function applyLabelDelta(labelIds: readonly string[], d: LabelDelta): string[] {
  const s = new Set(labelIds);
  for (const l of d.remove) s.delete(l);
  for (const l of d.add) s.add(l);
  // Gmail invariants: TRASH and SPAM leave the inbox; trash and spam are exclusive.
  if (d.add.includes(SYSTEM.TRASH)) { s.delete(SYSTEM.INBOX); s.delete(SYSTEM.SPAM); }
  if (d.add.includes(SYSTEM.SPAM)) { s.delete(SYSTEM.INBOX); s.delete(SYSTEM.TRASH); }
  return [...s].toSorted();
}

/** Derived thread flags kept in sync with labelIds (denormalized for list queries). */
export function threadFlags(labelIds: readonly string[]) {
  return { inInbox: labelIds.includes(SYSTEM.INBOX), starred: labelIds.includes(SYSTEM.STARRED) };
}

/** Named triage actions → label deltas. The UI and shortcuts speak these. */
export const actions = {
  done: { add: [], remove: [SYSTEM.INBOX] },
  undone: { add: [SYSTEM.INBOX], remove: [] },
  markRead: { add: [], remove: [SYSTEM.UNREAD] },
  markUnread: { add: [SYSTEM.UNREAD], remove: [] },
  star: { add: [SYSTEM.STARRED], remove: [] },
  unstar: { add: [], remove: [SYSTEM.STARRED] },
  trash: { add: [SYSTEM.TRASH], remove: [SYSTEM.INBOX] },
  spam: { add: [SYSTEM.SPAM], remove: [SYSTEM.INBOX] },
} satisfies Record<string, LabelDelta>;

/** Inverse delta for undo, given the labels before the action. */
export function inverseDelta(before: readonly string[], d: LabelDelta): LabelDelta {
  const after = new Set(applyLabelDelta(before, d));
  const b = new Set(before);
  return { add: [...b].filter((l) => !after.has(l)), remove: [...after].filter((l) => !b.has(l)) };
}
